import { useQueryClient } from 'react-query';
import { useAppDispatch } from '../store/storeConfig';
import { hasAssignedLocations } from '../utils/funcs';
import { useReplicatorFrom } from './useReplicatorFrom';
import {
	companySettingsFilter,
	customersFilter,
	optsCustomer,
	optsProducts,
	optsSales,
	productFilterInitial,
	productFilterSubsequent,
	salesFilter,
} from '../integrations/pouch/opts';
import { ELocalDBNames, EQuerykeys } from '../models';
import { handleReplicateProduct } from '../store/slices/productSlice';
import { invalidators } from '../integrations/react_query/invalidators';
import { supportWebWorker, useReplicateAndProcessSales } from './useReplicateAndProcessSalesTo';
import React from 'react';
import { getSessionInfo } from '../utils/getSessionInfo';
import { useOnlineStatus } from './useOnline';
import { useAuthenticate } from './useAuthenticate';
import { saveSession } from '../utils/storage';
import { getEnvs } from '../utils/getEnvs';
import { useReplicateLocations } from './useReplicateLocations';
import { useReplicatorTo } from './useReplicateTo';
import { cashiersFilter, optsCashiers, paymentTypesFilter, optsPaymentTypes } from '../integrations/pouch/opts';

export const useRegisterReplicationHooks = () => {
	const dispatch = useAppDispatch();

	const queryClient = useQueryClient();

	const { userInfo } = getSessionInfo();

	const isOnline = useOnlineStatus();

	// const worker: Worker = new Worker(new URL('.././workers/syncAndProcessSales.ts', import.meta.url));

	const isAuthenticated = useAuthenticate();

	React.useEffect(() => {
		if (!isAuthenticated) {
			return;
		}

		if (!hasAssignedLocations(userInfo)) {
			return;
		}

		if (!supportWebWorker()) return;
		// worker.postMessage({ userInfo });

		// worker.onmessage = function (arg) {
		// 	console.log(arg);
		// };

		return () => {
			// worker.terminate();
		};
	}, [isOnline, isAuthenticated, userInfo]);

	// Location sync runs post-selection (locationId guard inside the hook)
	useReplicateLocations();

	//todo: analyse the registered sales processor hook

	useReplicatorFrom({
		localDBName: `${userInfo?.profile?.email}-${ELocalDBNames.sales}`,
		filterInitial: salesFilter(),
		filterSubsequent: salesFilter(),
		filterFunc: optsSales,
		handleReplicationStatus: (v: boolean) => () => {}, //change this later,
		updateOnChange: invalidators(queryClient)[EQuerykeys.sales],
	});

	useReplicatorFrom({
		localDBName: ELocalDBNames.product,
		filterInitial: productFilterInitial(),
		filterSubsequent: productFilterSubsequent(),
		filterFunc: optsProducts,
		handleReplicationStatus: (v: boolean) => dispatch(handleReplicateProduct(v)),
		updateOnChange: invalidators(queryClient)[EQuerykeys.product],
	});

	useReplicatorFrom({
		localDBName: ELocalDBNames.cashiers,
		filterInitial: cashiersFilter(),
		filterSubsequent: cashiersFilter(),
		filterFunc: optsCashiers,
		handleReplicationStatus: (v: boolean) => () => {}, //change this later,
		updateOnChange: invalidators(queryClient)[EQuerykeys.cashier],
	});

	useReplicatorFrom({
		localDBName: ELocalDBNames.company_settings,
		filterInitial: companySettingsFilter(),
		filterSubsequent: companySettingsFilter(),
		handleReplicationStatus: (v: boolean) => () => {}, //change this later,
		updateOnChange: async (doc) => {
			const maybeSettingDocs = (doc?.[0] ?? {}) as any;

			const { _revisions, _rev, ...companySettingsDoc } = maybeSettingDocs ?? ({} as any);

			const { companySettings } = getEnvs();

			if (companySettingsDoc) {
				saveSession(companySettings, companySettingsDoc ?? {});
			}
		},
	});

	useReplicatorFrom({
		localDBName: ELocalDBNames.customer,
		filterInitial: customersFilter(),
		filterSubsequent: customersFilter(),
		filterFunc: optsCustomer,
		handleReplicationStatus: (v: boolean) => () => {}, //change this later,
		updateOnChange: invalidators(queryClient)[EQuerykeys.customer],
	});

	useReplicatorTo({
		localDBName: ELocalDBNames.customer,
		handleReplicationStatus: (v: boolean) => () => {}, //change this later,
		updateOnChange: invalidators(queryClient)[EQuerykeys.customer],
	});

	useReplicatorFrom({
		localDBName: ELocalDBNames.payment_types,
		filterInitial: paymentTypesFilter(),
		filterSubsequent: paymentTypesFilter(),
		filterFunc: optsPaymentTypes,
		handleReplicationStatus: (v: boolean) => () => {}, //change this later,
		updateOnChange: invalidators(queryClient)[EQuerykeys.payment_types],
	});

	useReplicateAndProcessSales();
};
